import React, { useState, useEffect } from 'react';
import { authorizedFetch } from '../utils/api';
import { ChevronLeftIcon, ChevronRightIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { HeartIcon, ShoppingCartIcon, EyeIcon } from '@heroicons/react/24/solid';

// Product Card Component
function ProductCard({ product, onAddToCart, onToggleFavorite, onQuickView }) {
    const [isFavorited, setIsFavorited] = useState(product.isFavorited || false);
    const [isLoading, setIsLoading] = useState(false);

    const handleFavoriteClick = async () => {
        setIsLoading(true);
        setIsFavorited(!isFavorited);
        await onToggleFavorite(product.id, !isFavorited);
        setIsLoading(false);
    };

    const handleAddToCart = async () => {
        setIsLoading(true);
        await onAddToCart(product);
        setIsLoading(false);
    };

    return (
        <div className="group relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
            {/* Product Image */}
            <div className="relative aspect-[3/4] overflow-hidden">
                <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={e => {
                        e.target.onerror = null;
                        e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
                    }}
                />

                {/* Overlay with quick actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300">
                    <div className="absolute top-2 right-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                            onClick={handleFavoriteClick}
                            disabled={isLoading}
                            className={`p-2 rounded-full shadow-lg transition-colors ${isFavorited ? 'bg-red-500 text-white' : 'bg-white text-gray-600 hover:text-red-500'
                                } disabled:opacity-50`}
                            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                            <HeartIcon className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => onQuickView(product)}
                            className="p-2 bg-white text-gray-600 hover:text-blue-500 rounded-full shadow-lg transition-colors"
                            title="Quick view"
                        >
                            <EyeIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Badges */}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {product.isNew && (
                        <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                            New
                        </span>
                    )}
                    {product.discount && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                            -{product.discount}%
                        </span>
                    )}
                    {product.isLimited && (
                        <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                            Limited
                        </span>
                    )}
                </div>
            </div>

            {/* Product Info */}
            <div className="p-4">
                <div className="mb-2">
                    <h3 className="font-semibold text-lg text-gray-900 line-clamp-2 mb-1">
                        {product.name}
                    </h3>
                    <p className="text-sm text-gray-600">{product.brand}</p>
                </div>

                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">
                            ${product.price}
                        </span>
                        {product.originalPrice && (
                            <span className="text-sm text-gray-500 line-through">
                                ${product.originalPrice}
                            </span>
                        )}
                    </div>
                    {product.rating && (
                        <div className="flex items-center gap-1">
                            <span className="text-yellow-400">★</span>
                            <span className="text-sm text-gray-600">{product.rating}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1">
                        {product.colors?.map((color, index) => (
                            <div
                                key={index}
                                className="w-4 h-4 rounded-full border border-gray-300"
                                style={{ backgroundColor: color }}
                                title={`Color option ${index + 1}`}
                            />
                        ))}
                    </div>
                    <span className="text-xs text-gray-500">
                        {product.sizes?.join(', ')}
                    </span>
                </div>

                <button
                    onClick={handleAddToCart}
                    disabled={isLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    <ShoppingCartIcon className="w-4 h-4" />
                    {isLoading ? 'Adding...' : 'Add to Cart'}
                </button>
            </div>
        </div>
    );
}

// Filter Sidebar Component
function FilterSidebar({ filters, onFilterChange, isOpen, onClose }) {
    const categories = ['Dresses', 'Tops', 'Bottoms', 'Outerwear', 'Accessories', 'Shoes'];
    const brands = ['Zara', 'H&M', 'Uniqlo', 'Nike', 'Adidas', 'Gucci', 'Prada', 'Chanel'];
    const priceRanges = [
        { label: 'Under $50', min: 0, max: 50 },
        { label: '$50 - $100', min: 50, max: 100 },
        { label: '$100 - $200', min: 100, max: 200 },
        { label: '$200 - $500', min: 200, max: 500 },
        { label: 'Over $500', min: 500, max: Infinity }
    ];

    return (
        <div className={`fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-lg transform transition-transform duration-300 lg:relative lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
            <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Filters</h2>
                    <button
                        onClick={onClose}
                        className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                    >
                        ×
                    </button>
                </div>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
                {/* Categories */}
                <div>
                    <h3 className="font-medium mb-3">Categories</h3>
                    <div className="space-y-2">
                        {categories.map(category => (
                            <label key={category} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={filters.categories?.includes(category)}
                                    onChange={(e) => {
                                        const newCategories = e.target.checked
                                            ? [...(filters.categories || []), category]
                                            : (filters.categories || []).filter(c => c !== category);
                                        onFilterChange({ ...filters, categories: newCategories });
                                    }}
                                    className="mr-2"
                                />
                                <span className="text-sm">{category}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Brands */}
                <div>
                    <h3 className="font-medium mb-3">Brands</h3>
                    <div className="space-y-2">
                        {brands.map(brand => (
                            <label key={brand} className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={filters.brands?.includes(brand)}
                                    onChange={(e) => {
                                        const newBrands = e.target.checked
                                            ? [...(filters.brands || []), brand]
                                            : (filters.brands || []).filter(b => b !== brand);
                                        onFilterChange({ ...filters, brands: newBrands });
                                    }}
                                    className="mr-2"
                                />
                                <span className="text-sm">{brand}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Price Range */}
                <div>
                    <h3 className="font-medium mb-3">Price Range</h3>
                    <div className="space-y-2">
                        {priceRanges.map((range, index) => (
                            <label key={index} className="flex items-center">
                                <input
                                    type="radio"
                                    name="priceRange"
                                    checked={filters.priceRange?.min === range.min && filters.priceRange?.max === range.max}
                                    onChange={() => onFilterChange({ ...filters, priceRange: range })}
                                    className="mr-2"
                                />
                                <span className="text-sm">{range.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Clear Filters */}
                <button
                    onClick={() => onFilterChange({})}
                    className="w-full py-2 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Clear All Filters
                </button>
            </div>
        </div>
    );
}

// Main Products Component
export default function Products() {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(24);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [filters, setFilters] = useState({});
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Filter and search products
    useEffect(() => {
        let filtered = [...products];

        // Apply search
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.brand.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply category filter
        if (filters.categories?.length) {
            filtered = filtered.filter(product =>
                filters.categories.includes(product.category)
            );
        }

        // Apply brand filter
        if (filters.brands?.length) {
            filtered = filtered.filter(product =>
                filters.brands.includes(product.brand)
            );
        }

        // Apply price range filter
        if (filters.priceRange) {
            filtered = filtered.filter(product =>
                product.price >= filters.priceRange.min &&
                product.price <= filters.priceRange.max
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price-low':
                    return a.price - b.price;
                case 'price-high':
                    return b.price - a.price;
                case 'rating':
                    return (b.rating || 0) - (a.rating || 0);
                case 'name':
                default:
                    return a.name.localeCompare(b.name);
            }
        });

        setFilteredProducts(filtered);
        setCurrentPage(1);
    }, [products, searchTerm, filters, sortBy]);

    const handleAddToCart = async (product) => {
        console.log('Adding to cart:', product);
        // Implement cart functionality
    };

    const handleToggleFavorite = async (productId, isFavorited) => {
        setProducts(prev => prev.map(p =>
            p.id === productId ? { ...p, isFavorited } : p
        ));
    };

    const handleQuickView = (product) => {
        setSelectedProduct(product);
    };

    // Pagination
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <div className="animate-pulse">
                <div className="h-8 bg-gray-300 rounded mb-4"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                    {[...Array(24)].map((_, i) => (
                        <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <div className="aspect-[3/4] bg-gray-300"></div>
                            <div className="p-4">
                                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded mb-1"></div>
                                <div className="h-3 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="flex">
            {/* Filter Sidebar */}
            <FilterSidebar
                filters={filters}
                onFilterChange={setFilters}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
            />

            {/* Main Content */}
            <div className="flex-1 lg:ml-0">
                {/* Header */}
                <div className="bg-white p-4 shadow-sm mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Search and Filter */}
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsFilterOpen(true)}
                                className="lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                <FunnelIcon className="w-4 h-4" />
                                Filters
                            </button>

                            <div className="relative flex-1 lg:w-80">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Sort and Results Count */}
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-600">
                                {filteredProducts.length} products
                            </span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="name">Sort by Name</option>
                                <option value="price-low">Price: Low to High</option>
                                <option value="price-high">Price: High to Low</option>
                                <option value="rating">Highest Rated</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Products Grid */}
                <div className="px-4">
                    {currentProducts.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">🛍️</div>
                            <h3 className="text-lg font-medium mb-2">No products found</h3>
                            <p className="text-sm text-gray-600">Try adjusting your search or filters</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mb-8">
                            {currentProducts.map((product) => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    onAddToCart={handleAddToCart}
                                    onToggleFavorite={handleToggleFavorite}
                                    onQuickView={handleQuickView}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg shadow-sm">
                            <div className="flex flex-1 justify-between sm:hidden">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${currentPage === 1
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${currentPage === totalPages
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>

                            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                                        <span className="font-medium">
                                            {Math.min(endIndex, filteredProducts.length)}
                                        </span>{' '}
                                        of <span className="font-medium">{filteredProducts.length}</span> results
                                    </p>
                                </div>

                                <div>
                                    <nav aria-label="Pagination" className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`relative inline-flex items-center rounded-l-md px-2 py-2 ${currentPage === 1
                                                    ? 'text-gray-300 cursor-not-allowed'
                                                    : 'text-gray-400 hover:bg-gray-50'
                                                } ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0`}
                                        >
                                            <ChevronLeftIcon className="h-5 w-5" />
                                        </button>

                                        {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                                            const page = index + 1;
                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => handlePageChange(page)}
                                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${currentPage === page
                                                            ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`relative inline-flex items-center rounded-r-md px-2 py-2 ${currentPage === totalPages
                                                    ? 'text-gray-300 cursor-not-allowed'
                                                    : 'text-gray-400 hover:bg-gray-50'
                                                } ring-1 ring-inset ring-gray-300 focus:z-20 focus:outline-offset-0`}
                                        >
                                            <ChevronRightIcon className="h-5 w-5" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Overlay for mobile filter */}
            {isFilterOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setIsFilterOpen(false)}
                />
            )}
        </div>
    );
}